import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import {
  displayItemTitle,
  type PictureTalkSceneItem,
} from '@/modules/learning/api/pictureTalk';

export type PictureTalkSceneViewerProps = {
  imageUrl: string;
  /** Intrinsic aspect ratio (width / height) when known; else 4/3 fallback. */
  imageWidth?: number;
  imageHeight?: number;
  items: PictureTalkSceneItem[];
  selectedItemId: string | null;
  onItemSelect: (item: PictureTalkSceneItem) => void;
  /** When true, hotspot taps are ignored (e.g. while live session is connecting). */
  disabled?: boolean;
  /** Primary hero image failed to load (e.g. signed URL expired); page may refetch scene once. */
  onHeroPrimaryLoadFailed?: () => void;
};

/**
 * Hotspots use normalized bbox (0–1, top-left + size) in **image bitmap** space.
 * The image uses `object-contain`; overlay positions are derived from the **rendered**
 * image rectangle inside this wrapper (letterbox-safe).
 */
type ContentBox = { left: number; top: number; width: number; height: number };

const defaultBox: ContentBox = { left: 0, top: 0, width: 1, height: 1 };

export const PictureTalkSceneViewer: React.FC<PictureTalkSceneViewerProps> = ({
  imageUrl,
  imageWidth,
  imageHeight,
  items,
  selectedItemId,
  onItemSelect,
  disabled = false,
  onHeroPrimaryLoadFailed,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<ContentBox>(defaultBox);
  const lastFireRef = useRef(0);

  const updateLayout = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const img = wrap.querySelector('img');
    if (!img || !img.complete || img.naturalWidth === 0) return;
    const wr = wrap.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    if (wr.width <= 0 || wr.height <= 0) return;
    setBox({
      left: (ir.left - wr.left) / wr.width,
      top: (ir.top - wr.top) / wr.height,
      width: ir.width / wr.width,
      height: ir.height / wr.height,
    });
  }, []);

  useLayoutEffect(() => {
    updateLayout();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => updateLayout());
    ro.observe(wrap);
    window.addEventListener('resize', updateLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateLayout);
    };
  }, [updateLayout, imageUrl, imageWidth, imageHeight]);

  const fire = (item: PictureTalkSceneItem) => {
    const now = Date.now();
    if (now - lastFireRef.current < 250) return;
    lastFireRef.current = now;
    onItemSelect(item);
  };

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const areaA = a.bbox.w * a.bbox.h;
        const areaB = b.bbox.w * b.bbox.h;
        return areaB - areaA;
      }),
    [items]
  );

  const aspectStyle =
    imageWidth && imageHeight && imageWidth > 0 && imageHeight > 0
      ? { aspectRatio: `${imageWidth} / ${imageHeight}` as const }
      : undefined;

  return (
    <div
      ref={wrapRef}
      className="relative w-full max-w-4xl mx-auto overflow-hidden rounded-xl border-2 border-amber-200 bg-slate-900/5 shadow-lg"
      style={aspectStyle ?? { aspectRatio: '4 / 3' }}
    >
      <ImageWithFallback
        src={imageUrl}
        alt=""
        className="block h-full w-full object-contain select-none"
        onLoad={updateLayout}
        onPrimaryLoadFailed={onHeroPrimaryLoadFailed}
      />

      {/* Phase 1: polygon_norm reserved — same mapping as bbox when implemented. */}
      <div className="pointer-events-none absolute inset-0">
        {sorted.map((item, idx) => {
          const { bbox } = item;
          const left = (box.left + bbox.x * box.width) * 100;
          const top = (box.top + bbox.y * box.height) * 100;
          const width = bbox.w * box.width * 100;
          const height = bbox.h * box.height * 100;
          const isSel = selectedItemId === item.id;
          const z = 100 + (1000 - idx);

          return (
            <button
              key={item.id}
              type="button"
              aria-label={displayItemTitle(item)}
              disabled={disabled}
              className={[
                disabled ? 'pointer-events-none' : 'pointer-events-auto',
                'absolute box-border min-h-[44px] min-w-[44px] rounded-md border-2 transition-colors',
                disabled ? 'cursor-not-allowed opacity-50' : '',
                isSel
                  ? 'border-orange-500 bg-orange-400/25 ring-2 ring-orange-400 ring-offset-1'
                  : 'border-white/70 bg-white/10 hover:bg-white/20 active:bg-white/30',
              ].join(' ')}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
                zIndex: z,
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (disabled) return;
                fire(item);
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (disabled) return;
                fire(item);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
